import React, { useState, useRef, forwardRef } from 'react';
import { Box, Typography } from '@mui/material';
import * as Reorder from '../../../../components/dnd/Reorder';
import { IProfession } from './constants';
import SingleProfessionEntry from './SingleProfessionEntry';
import SingleProfessionInput from './SingleProfessionInput';
import { useMergePendingItems, useMergePendingItemsHandle } from '../../../../hooks/useMergePendingItems';

interface Props {
  professions: IProfession[];
  updateProfessions: (professions: IProfession[]) => void;
  inputProps?: any;
  isApplicationForm?: boolean;
}

export default forwardRef<{ mergePendingItems: () => IProfession[] }, Props>(
  function FormProfessions({ 
    professions = [], 
    updateProfessions,
    inputProps = {},
    isApplicationForm = false
  }, ref) {
    const [editableIndex, setEditableIndex] = useState(-1);
    const inputRef = useRef<{ getCurrentValue: () => IProfession | null; clear: () => void }>(null);

    const isDuplicateProfession = (profession: IProfession, list: IProfession[]): boolean => {
      return list.some(existing => 
        existing.profession.id === profession.profession.id &&
        (existing.subProfession?.id === profession.subProfession?.id || 
         (!existing.subProfession && !profession.subProfession))
      );
    };

    const { pendingItems, mergePendingItems, addToPending, clearPending } = useMergePendingItems<IProfession, IProfession>({
      items: professions,
      updateItems: updateProfessions,
      inputRef,
      mergeOrder: 'prepend',
      isDuplicate: isDuplicateProfession,
    });

    useMergePendingItemsHandle(ref, mergePendingItems);

    const handleAddToPending = (profession: IProfession) => {
      // Check if already in pending items or existing professions
      if (isDuplicateProfession(profession, pendingItems) || isDuplicateProfession(profession, professions)) {
        return; // Don't add if duplicate
      }
      addToPending(profession);
    };

    const addSingleProfession = (profession: IProfession) => {
      // Check if already exists
      if (isDuplicateProfession(profession, professions)) {
        return; // Don't add if duplicate
      }
      const allItems = [...pendingItems, profession];
      updateProfessions([...allItems, ...professions]);
      clearPending();
      setEditableIndex(-1);
    };

  const updateSingleProfession = (profession: IProfession, index: number) => {
    const tempList = [...professions];
    tempList[index] = profession;
    const newProfessions = [...tempList];
    updateProfessions(newProfessions);
    setEditableIndex(-1);
  };

  const removeSingleProfession = (index: number) => {
    const tempList = [...professions];
    tempList.splice(index, 1);
    const newProfessions = [...tempList];
    updateProfessions(newProfessions);
  };


  return (
    <Box className="flex flex-col gap-2 w-full">
      <SingleProfessionInput 
        ref={inputRef}
        {...inputProps} 
        onChange={addSingleProfession}
        onAddToPending={handleAddToPending}
      />

      {pendingItems.length > 0 && (
        <Box className="flex flex-col gap-2 w-full mb-2">
          {pendingItems.map((item, index) => (
            <Box key={`pending-${index}`} className="p-2 border border-dashed border-border-token rounded-lg bg-surface-2">
              <Typography className="text-text-1">
                {item.profession?.name || 'Unknown'}
                {item.subProfession?.name && ` - ${item.subProfession.name}`}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Reorder.Root options={professions} onUpdateOrder={updateProfessions}>
        {professions.map((item, index) => {
          if (editableIndex === index) {
            return (
              <SingleProfessionInput
                {...inputProps}
                key={`interest-${item}-${index}`}
                profession={item}
                onChange={(text) => updateSingleProfession(text, index)}
                onCross={() => setEditableIndex(-1)}
              />
            );
          }

          return (
            <SingleProfessionEntry
              key={index}
              index={index}
              profession={item}
              onEditProfession={setEditableIndex}
              onRemoveProfession={removeSingleProfession}
            />
          );
        })}
      </Reorder.Root>
    </Box>
  );
});
